<script setup>
// InteliChat topbar: workspace title, global search (opens the real
// Cmd/Ctrl+K InteliSearch overlay - live customers/orders/parts), theme
// toggle (drives the NATIVE Chatwoot theme mechanism so both worlds
// stay in sync + persist via localStorage), and notifications (native
// notifications screen).
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper';
import { useInteliAccount } from '../composables/useInteliAccount';
import { useInteliSearch } from '../composables/useInteliSearch';

const route = useRoute();
const router = useRouter();
const { accountId } = useInteliAccount();
const { open: openSearch } = useInteliSearch();

// Native pages render inside the persistent IC chrome and carry no
// inteliTitle meta - derive a section title from the path instead.
const NATIVE_TITLES = [
  ['/settings', 'Settings'],
  ['/reports', 'Reports'],
  ['/contacts', 'Contacts'],
  ['/notifications', 'Notifications'],
  ['/custom_view', 'Folders'],
  ['/label/', 'Labels'],
  ['/team/', 'Teams'],
  ['/mentions', 'Mentions'],
  ['/unattended', 'Unattended'],
  ['/participating', 'Participating'],
  ['/conversations', 'Conversations'],
  ['/dashboard', 'Conversations'],
];

const title = computed(() => {
  if (route.meta?.inteliTitle) return route.meta.inteliTitle;
  const hit = NATIVE_TITLES.find(([seg]) => route.path.includes(seg));
  return hit ? hit[1] : 'InteliChat';
});

const isMac = /mac/i.test(window.navigator?.platform || '');
const searchHint = isMac ? '⌘K' : 'Ctrl K';

const openNotifications = () => {
  router.push({
    name: 'notifications_index',
    params: { accountId: accountId.value },
  });
};

// theme: reuse Chatwoot's storage key + helper so the toggle applies to
// the whole app (native screens included), not an InteliChat-only fork.
const isDark = ref(false);

const syncIsDark = () => {
  isDark.value = document.body.classList.contains('dark');
};

onMounted(syncIsDark);

const toggleTheme = () => {
  const next = isDark.value ? 'light' : 'dark';
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, next);
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
  syncIsDark();
};
</script>

<template>
  <header class="il-top">
    <h2 data-il="workspace-title">{{ title }}</h2>
    <button class="il-search" data-il="search" @click="openSearch">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      <span class="ph">Search customers, orders, parts</span>
      <kbd class="il-mono">{{ searchHint }}</kbd>
    </button>
    <div class="il-top-r">
      <button
        class="il-ib"
        data-il="theme-toggle"
        :title="isDark ? 'Switch to light' : 'Switch to dark'"
        @click="toggleTheme"
      >
        <svg
          v-if="isDark"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <circle cx="12" cy="12" r="4" />
          <path
            d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"
          />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      </button>
      <button
        class="il-ib"
        data-il="notifications"
        title="Notifications"
        @click="openNotifications"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
        >
          <path
            d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0"
          />
        </svg>
      </button>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.il-top {
  position: relative;
  height: 62px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 0 26px;
  background: var(--il-card);

  /* metallic bottom edge - replaces the flat hairline */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background: var(--il-metal-gradient);
  }

  h2 {
    font-size: 17px;
    font-weight: 700;
    color: var(--il-ink);
    white-space: nowrap;
  }
}

.il-search {
  flex: 1;
  max-width: 400px;
  display: flex;
  align-items: center;
  gap: 9px;
  background: var(--il-paper);
  border: 1px solid var(--il-line);
  border-radius: var(--il-r-s);
  padding: 9px 13px;
  text-align: left;

  &:hover {
    border-color: var(--il-silver);
  }

  svg {
    width: 16px;
    height: 16px;
    color: var(--il-ink-3);
    flex-shrink: 0;
  }

  .ph {
    flex: 1;
    font-size: 13px;
    color: var(--il-ink-4);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  kbd {
    font-size: 11px;
    color: var(--il-ink-2);
    background: var(--il-silver-soft);
    border: 1px solid var(--il-silver);
    border-radius: 5px;
    padding: 1px 6px;
  }
}

.il-top-r {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.il-ib {
  width: 38px;
  height: 38px;
  border-radius: var(--il-r-s);
  display: grid;
  place-items: center;
  color: var(--il-ink-2);

  &:hover {
    background: var(--il-paper);
    color: var(--il-ink);
  }

  svg {
    width: 19px;
    height: 19px;
  }
}

/* phone (<768px, mobile spec): safe-area top inset (notch), 44px touch
   targets, search collapses to its icon (opens the full-screen
   InteliSearch overlay). */
@media (max-width: 767.98px) {
  .il-top {
    height: auto;
    min-height: 56px;
    padding: env(safe-area-inset-top, 0px) 12px 0;
    gap: 10px;

    h2 {
      font-size: 16px;
    }
  }

  .il-search {
    flex: 0 0 auto;
    min-width: 44px;
    min-height: 44px;
    justify-content: center;
    padding: 9px;

    .ph,
    kbd {
      display: none;
    }
  }

  .il-ib {
    width: 44px;
    height: 44px;
  }
}
</style>
